import { useEffect, useState, useCallback, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import { DateRangeFilter, ExportBar } from '../components/Filters';
import { ColFilter } from '../components/ColFilter';

const STATUS_COLORS = {
  'SUCCESS':                  '#44BBA4',
  'FAILED: INC-20A REQUIRED': '#E8C547',
  'FAILED':                   '#E84855',
  'SESSION_DEAD':             '#A23B72',
};
const STATUS_ICONS = {
  'SUCCESS':                  '✓',
  'FAILED: INC-20A REQUIRED': '!',
  'FAILED':                   '✕',
  'SESSION_DEAD':             '⊘',
};

export default function MGT7A() {
    // Redirect to login page if DevTools or View Source shortcuts are pressed
    useEffect(() => {
      const handleKeyDown = (e) => {
        // F12
        if (e.key === 'F12') {
          window.location.href = '/login';
          e.preventDefault();
        }
        // Ctrl+Shift+I or Cmd+Opt+I
        if ((e.ctrlKey || e.metaKey) && e.shiftKey && (e.key === 'I' || e.key === 'i')) {
          window.location.href = '/login';
          e.preventDefault();
        }
        // Ctrl+U or Cmd+U
        if ((e.ctrlKey || e.metaKey) && (e.key === 'U' || e.key === 'u')) {
          window.location.href = '/login';
          e.preventDefault();
        }
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  const nav = useNavigate();
  const { can } = useAuth();
  
  const getSaved = (k, d) => { try { const v = sessionStorage.getItem('mgt_'+k); return v!==null ? JSON.parse(v) : d; } catch { return d; } };

  const [records,  setRecords]  = useState([]);
  const [total,    setTotal]    = useState(0);
  const [page,     setPage]     = useState(() => getSaved('page', 1));
  const [loading,  setLoading]  = useState(true);
  const [statuses, setStatuses] = useState([]);
  const [search,   setSearch]   = useState(() => getSaved('search', ''));
  const [dSearch,  setDSearch]  = useState(() => getSaved('search', ''));
  const [dateFilters, setDateFilters] = useState(() => getSaved('date', { fromMonth:'', fromYear:'', toMonth:'', toYear:'' }));

  // Column filters (client-side on current page; GST/IEC are YES/NO)
  const [colGst,  setColGst]  = useState(() => getSaved('gst', ''));
  const [colIec,  setColIec]  = useState(() => getSaved('iec', ''));
  const [colStatus, setColStatus] = useState(() => getSaved('status', ''));

  useEffect(() => {
    sessionStorage.setItem('mgt_page', JSON.stringify(page));
    sessionStorage.setItem('mgt_search', JSON.stringify(search));
    sessionStorage.setItem('mgt_date', JSON.stringify(dateFilters));
    sessionStorage.setItem('mgt_gst', JSON.stringify(colGst));
    sessionStorage.setItem('mgt_iec', JSON.stringify(colIec));
    sessionStorage.setItem('mgt_status', JSON.stringify(colStatus));
  }, [page, search, dateFilters, colGst, colIec, colStatus]);

  const status = searchParams.get('status') || '';
  const limit  = 50;

  useEffect(() => {
    const t = setTimeout(() => {
      if (search !== dSearch) {
        setDSearch(search); setPage(1);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [search, dSearch]);

  const buildParams = useCallback(() => {
    const p = { page, limit };
    if (status) p.status = status;
    if (dSearch) p.search = dSearch;
    Object.assign(p, dateFilters.fromMonth ? { fromMonth: dateFilters.fromMonth } : {});
    Object.assign(p, dateFilters.fromYear  ? { fromYear:  dateFilters.fromYear  } : {});
    Object.assign(p, dateFilters.toMonth   ? { toMonth:   dateFilters.toMonth   } : {});
    Object.assign(p, dateFilters.toYear    ? { toYear:    dateFilters.toYear    } : {});
    
    // Server-side filters
    if (colGst)  p.hasGst  = colGst;
    if (colIec)  p.hasIec  = colIec;
    if (colStatus) p.incStatus = colStatus; // Extra INC-20A secondary status filter if any
    
    return p;
  }, [page, status, dSearch, dateFilters, colGst, colIec, colStatus]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [res, st] = await Promise.all([
        api.mgt7a.records(buildParams()),
        api.mgt7a.status(),
      ]);
      setRecords(res.rows);
      setTotal(res.total);
      setStatuses(st);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [buildParams]);

  useEffect(() => { loadData(); }, [loadData]);

  // Make filteredRows just point to records
  const filteredRows = records;

  useEffect(() => { setPage(1); }, [colGst, colIec, colStatus]);

  const handleExport = (fmt) => {
    const p = buildParams(); delete p.page; delete p.limit; p.format = fmt;
    api.mgt7a.export(p);
  };

  const clearAll = () => {
    setSearch(''); setDateFilters({ fromDate:'', toDate:'' });
    setColGst(''); setColIec(''); setColStatus('');
    setSearchParams({}); setPage(1);
  };

  const hasFilters = status || dSearch || dateFilters.fromDate || dateFilters.toDate || colGst || colIec || colStatus;
  const totalPages = Math.ceil(total / limit);
  const fmt = n => n == null ? '—' : Number(n).toLocaleString('en-IN');

  // Unique status values from current page for col filter options
  const statusOptions = [...new Set(records.map(r => r.INC_20A_STATUS).filter(Boolean))].map(v => ({ value: v, label: v }));

  // Disable right-click context menu
  const handleContextMenu = (e) => {
    e.preventDefault();
  };

  return (
    <div className="page" onContextMenu={handleContextMenu}>
      <div className="page-header">
        <div>
          <h1 className="page-title">MGT-7A Tracking</h1>
          <p className="page-desc">INC-20A enrichment pipeline status for all CINs</p>
        </div>
        {hasFilters && (
          <button className="btn-clear-all" onClick={clearAll}>✕ Clear all filters</button>
        )}
      </div>

      {/* ── Status pills ── */}
      <div className="filter-bar">
        <button
          className={`filter-pill ${!status ? 'active' : ''}`}
          onClick={() => { setSearchParams({}); setPage(1); setColStatus(''); }}
        >
          All
          <span className="pill-count">{statuses.reduce((a, s) => a + Number(s.CNT), 0).toLocaleString('en-IN')}</span>
        </button>
        {statuses.map(s => (
          <button
            key={s.INC_20A_STATUS}
            className={`filter-pill ${status === s.INC_20A_STATUS ? 'active' : ''}`}
            style={{ '--pill-active-bg': STATUS_COLORS[s.INC_20A_STATUS] + '22',
                     '--pill-active-color': STATUS_COLORS[s.INC_20A_STATUS] }}
            onClick={() => { setSearchParams({ status: s.INC_20A_STATUS }); setPage(1); setColStatus(''); }}
          >
            <span className="pill-dot" style={{ background: STATUS_COLORS[s.INC_20A_STATUS] || '#888' }} />
            {s.INC_20A_STATUS}
            <span className="pill-count">{Number(s.CNT).toLocaleString('en-IN')}</span>
          </button>
        ))}
      </div>

      {/* ── Search + date filter ── */}
      <div className="filter-section" style={{ marginTop: '0.75rem' }}>
        <div className="filter-row">
          <input
            className="search-input"
            placeholder="Search by CIN or company name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {dSearch && (
            <button className="search-clear" onClick={() => { setSearch(''); setDSearch(''); }}>✕</button>
          )}
        </div>
        <DateRangeFilter
          filters={dateFilters}
          onChange={f => { setDateFilters(f); setPage(1); }}
          label="EXTRACTION DATE"
        />
      </div>

      <ExportBar onExport={handleExport} total={total} canExport={can('export')} />

      {/* ── Table ── */}
      <div className="table-card" style={{ marginTop: '12px' }}>
        {loading ? (
          <div className="table-loading"><div className="spinner" /></div>
        ) : (
          <>
            <div className="table-meta-bar">
              <span>
                {colGst || colIec || colStatus
                  ? <><strong>{filteredRows.length}</strong> of {fmt(total)} records (column filter active)</>
                  : <><strong>{fmt(total)}</strong> records · Page {page} of {totalPages || 1}</>}
              </span>
            </div>

            {filteredRows.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">🔍</div>
                <div className="empty-state-title">No Records Found</div>
                <div className="empty-state-desc">
                  {dSearch
                    ? `No results for "${dSearch}". Try a different CIN or company name.`
                    : 'No records match the current filters.'}
                </div>
                <button className="empty-state-btn" onClick={clearAll}>Clear Filters</button>
              </div>
            ) : (
              <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>CIN</th>
                      <th>Company Name</th>
                      <th>
                        <ColFilter
                          label="INC-20A Status"
                          options={statusOptions}
                          value={colStatus}
                          onChange={v => setColStatus(v)}
                          width={240}
                        />
                      </th>
                      <th>PAN</th>
                      <th>
                        <ColFilter
                          label="GST"
                          options={[{ value: 'YES', label: '✓ Has GST' }, { value: 'NO', label: '✕ No GST' }]}
                          value={colGst}
                          onChange={v => setColGst(v)}
                          width={140}
                        />
                      </th>
                      <th>
                        <ColFilter
                          label="IEC"
                          options={[{ value: 'YES', label: '✓ Has IEC' }, { value: 'NO', label: '✕ No IEC' }]}
                          value={colIec}
                          onChange={v => setColIec(v)}
                          width={140}
                        />
                      </th>
                      <th>Extracted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map(r => (
                      <tr key={r.CIN} onClick={() => nav(`/companies/${r.CIN}`)} style={{ cursor: 'pointer' }}>
                        <td className="mono">{r.CIN}</td>
                        <td className="td-company">{r.COMPANY_NAME || '—'}</td>
                        <td>
                          <span className="inc-status-pill" style={{
                            background: (STATUS_COLORS[r.INC_20A_STATUS] || '#888') + '18',
                            color: STATUS_COLORS[r.INC_20A_STATUS] || '#888',
                            borderColor: (STATUS_COLORS[r.INC_20A_STATUS] || '#888') + '55',
                          }}>
                            <span>{STATUS_ICONS[r.INC_20A_STATUS] || '?'}</span>
                            {r.INC_20A_STATUS || '—'}
                          </span>
                        </td>
                        <td className="mono">{r.PAN || '—'}</td>
                        <td><span className={r.HAS_GST === 'YES' ? 'badge-yes' : 'badge-no'}>{r.HAS_GST}</span></td>
                        <td><span className={r.HAS_IEC === 'YES' ? 'badge-yes' : 'badge-no'}>{r.HAS_IEC}</span></td>
                        <td className="date-cell">
                          {r.EXTRACTION_DATE ? new Date(r.EXTRACTION_DATE).toLocaleDateString('en-IN') : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {filteredRows.length > 0 && (
              <div className="pagination">
                <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="page-btn">← Prev</button>
                <span className="page-info">Page {page} / {totalPages || 1}</span>
                <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="page-btn">Next →</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
